import { useSpeckleNotificationsStore } from "@haskoning/vue-speckle";
import { ref } from "vue";

import { useLoginSpeckle } from "@/composables/useLoginSpeckle";
import type { SpeckleWorkbookWriteTarget } from "@/excel/speckle/receiveTypes";
import type { ExtendedDocument, VisibleExcelProfile } from "@/services/open-api/openApi.types";
import {
  ReceiveWriteConfirmationRequiredError,
  writeSpeckleReceiveResponseToWorkbook,
} from "@/excel/speckle/receiveWriter";
import {
  getTransferProfileKey,
  type SpeckleTransferProfileOption,
} from "@/services/equation-excel-client-metadata/equationExcelClientMetadata";
import { receiveSpeckleData, type SpeckleReceiveResponse } from "@/services/speckle/speckleExcelApi";
import { useApiStore } from "@/stores/api/useApiStore";

import { useSpeckleContextSelection } from "./useSpeckleContextSelection";

export type PendingReceiveWrite = {
  profileKey: string;
  response: SpeckleReceiveResponse;
  excelProfile: VisibleExcelProfile;
  apiSchema: ExtendedDocument | undefined;
  target: SpeckleWorkbookWriteTarget;
  nonEmptyCellCount: number;
};

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function getWriteSuccessMessage(targetAddress?: string): string {
  return targetAddress
    ? `Speckle data received and written to ${targetAddress}.`
    : "Speckle data received and written to the selected Excel cell.";
}

export function useReceiveSpeckleProfile() {
  const speckleNotificationsStore = useSpeckleNotificationsStore();
  const { getSelectionContext } = useSpeckleContextSelection();
  const { ensureSpeckleSession } = useLoginSpeckle();
  const apiStore = useApiStore();

  const receivingProfileKey = ref<string | null>(null);

  const overwriteDialogOpen = ref(false);
  const pendingReceiveWrite = ref<PendingReceiveWrite | null>(null);
  const isWritingOverwrite = ref(false);

  async function receiveIntoWorkbook(option: SpeckleTransferProfileOption): Promise<void> {
    receivingProfileKey.value = getTransferProfileKey(option);
    pendingReceiveWrite.value = null;
    overwriteDialogOpen.value = false;

    try {
      if (!(await ensureSpeckleSession())) {
        return;
      }

      const context = getSelectionContext();

      if (!context) {
        speckleNotificationsStore.showMessage("Select a Speckle project, model and version before receiving data.", {
          color: "error",
        });
        return;
      }

      const response = await receiveSpeckleData(option.apiUrl, option, context.receiveUrl, context.speckleToken);

      const apiSchema = apiStore.getCachedSchema(option.apiUrl);

      try {
        const result = await writeSpeckleReceiveResponseToWorkbook(response, option, apiSchema);

        speckleNotificationsStore.showMessage(getWriteSuccessMessage(result.target.targetAddress), {
          color: "success",
        });
      } catch (writeError) {
        if (writeError instanceof ReceiveWriteConfirmationRequiredError) {
          pendingReceiveWrite.value = {
            profileKey: getTransferProfileKey(option),
            response,
            excelProfile: option,
            apiSchema,
            target: writeError.target,
            nonEmptyCellCount: writeError.nonEmptyCellCount,
          };
          overwriteDialogOpen.value = true;
          return;
        }

        throw writeError;
      }
    } catch (error) {
      console.error("Failed to receive Speckle data:", error);

      // The request runs for up to five minutes, so the session can die mid-flight. Only report a
      // plain failure once the session is ruled out — otherwise the banner already explains it.
      if (await ensureSpeckleSession()) {
        speckleNotificationsStore.showMessage(getErrorMessage(error, "Failed to receive Speckle data."), {
          color: "error",
        });
      }
    } finally {
      receivingProfileKey.value = null;
    }
  }

  function cancelReceiveOverwrite(): void {
    if (isWritingOverwrite.value) {
      return;
    }

    overwriteDialogOpen.value = false;
    pendingReceiveWrite.value = null;
  }

  async function confirmReceiveOverwrite(): Promise<void> {
    const pendingWrite = pendingReceiveWrite.value;

    if (!pendingWrite) {
      return;
    }

    isWritingOverwrite.value = true;
    receivingProfileKey.value = pendingWrite.profileKey;

    try {
      const result = await writeSpeckleReceiveResponseToWorkbook(
        pendingWrite.response,
        pendingWrite.excelProfile,
        pendingWrite.apiSchema,
        true,
        pendingWrite.target
      );

      speckleNotificationsStore.showMessage(getWriteSuccessMessage(result.target.targetAddress), {
        color: "success",
      });

      overwriteDialogOpen.value = false;
      pendingReceiveWrite.value = null;
    } catch (error) {
      speckleNotificationsStore.showMessage(getErrorMessage(error, "Failed to write Speckle data to Excel."), {
        color: "error",
      });
      console.error("Failed to overwrite the Speckle receive target range:", error);
    } finally {
      isWritingOverwrite.value = false;
      receivingProfileKey.value = null;
    }
  }

  return {
    receivingProfileKey,
    receiveIntoWorkbook,

    overwriteDialogOpen,
    pendingReceiveWrite,
    isWritingOverwrite,
    cancelReceiveOverwrite,
    confirmReceiveOverwrite,
  };
}
